import { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button, Table, Tabs, Tab, Badge, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiPencil, HiTrash } from 'react-icons/hi2';
import { useDateFormat } from '@/utils/dates';
import { PageContent } from '@/components/layout/PageLayout';
import DetailHeader from '@/components/layout/DetailHeader';
import { StatusBadge, HiddenFileInput, ActionsTh } from '@/styles/shared.styled';
import { DefinitionList, type DefinitionItem } from '@/components/common/DefinitionList';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import PosiedzenieFormModal from '@/components/sprawy/PosiedzenieFormModal';
import useConfirm from '@/hooks/useConfirm';
import {
  useGetSprawaQuery,
  useCreatePosiedzenieMutation,
  useUpdatePosiedzenieMutation,
  useDeletePosiedzenieMutation,
  useListTimeEntriesQuery,
} from '@/store/sprawyApi';
import TimeEntriesTab from '@/components/timeEntries/TimeEntriesTab';
import { useUploadDocumentMutation } from '@/store/documentsApi';
import { useToast } from '@/hooks/useToast';
import type { PosiedzenieDto, CreatePosiedzenieRequest } from '@/types';

export default function SprawaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate, formatDateTime } = useDateFormat();
  const { data: sprawa, isLoading, refetch: refetchSprawa } = useGetSprawaQuery(id!, { skip: !id });
  const { data: timeEntries = [] } = useListTimeEntriesQuery(id!, { skip: !id });

  const [createPosiedzenie] = useCreatePosiedzenieMutation();
  const [updatePosiedzenie] = useUpdatePosiedzenieMutation();
  const [deletePosiedzenie] = useDeletePosiedzenieMutation();
  const [uploadDocument] = useUploadDocumentMutation();
  const { showSuccess, showError } = useToast();
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [showPosiedzenieModal, setShowPosiedzenieModal] = useState(false);
  const [editingPosiedzenie, setEditingPosiedzenie] = useState<PosiedzenieDto | null>(null);
  const { confirm, confirmProps } = useConfirm();

  if (isLoading || !sprawa) {
    return <PageContent><LoadingSpinner /></PageContent>;
  }

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !id) return;

    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        await uploadDocument({ file, fileName: file.name, sprawaId: id }).unwrap();
      }
      showSuccess(t('sprawy.detail.uploadSuccess', { count: files.length }));
      refetchSprawa();
    } catch {
      showError(t('sprawy.detail.uploadError'));
    } finally {
      setIsUploading(false);
      if (documentInputRef.current) documentInputRef.current.value = '';
    }
  };

  const handleCreatePosiedzenie = async (_sprawaId: string, data: CreatePosiedzenieRequest) => {
    await createPosiedzenie({ sprawaId: id!, ...data }).unwrap();
  };

  const handleUpdatePosiedzenie = async (_sprawaId: string, data: CreatePosiedzenieRequest) => {
    if (!editingPosiedzenie) return;
    await updatePosiedzenie({ sprawaId: id!, id: editingPosiedzenie.id, ...data }).unwrap();
  };

  const handleDeletePosiedzenie = async (posiedzenie: PosiedzenieDto) => {
    const confirmed = await confirm({
      title: t('posiedzenia.deleteTitle'),
      message: t('posiedzenia.deleteConfirm'),
      variant: 'danger',
    });
    if (confirmed) {
      await deletePosiedzenie({ sprawaId: id!, id: posiedzenie.id }).unwrap();
    }
  };

  const openCreatePosiedzenie = () => {
    setEditingPosiedzenie(null);
    setShowPosiedzenieModal(true);
  };

  const openEditPosiedzenie = (p: PosiedzenieDto) => {
    setEditingPosiedzenie(p);
    setShowPosiedzenieModal(true);
  };

  const courtLink = sprawa.court && sprawa.portalCourtId
    ? <Link to={`/sprawy?courtId=${sprawa.portalCourtId}`}>{sprawa.court}</Link>
    : sprawa.court;

  const infoRows: DefinitionItem[] = [
    { label: t('sprawy.form.signature'), value: sprawa.signature },
    { label: t('sprawy.form.court'), value: courtLink },
    { label: t('sprawy.form.department'), value: sprawa.department },
    { label: t('sprawy.form.judge'), value: sprawa.judge },
    { label: t('sprawy.form.subject'), value: sprawa.subject },
    { label: t('sprawy.form.repertoryType'), value: sprawa.repertoryType },
    { label: t('sprawy.form.receiptDate'), value: sprawa.receiptDate ? formatDate(sprawa.receiptDate) : null },
    { label: t('sprawy.form.finishDate'), value: sprawa.finishDate ? formatDate(sprawa.finishDate) : null },
    { label: t('sprawy.form.portalStatus'), value: t(`sprawaStatus.${sprawa.portalStatus}`) },
    {
      label: t('sprawy.form.myStatus'),
      value: sprawa.statusName
        ? <StatusBadge $color={sprawa.statusColor ?? undefined}>{sprawa.statusName}</StatusBadge>
        : null,
    },
    { label: t('sprawy.form.value'), value: sprawa.value },
    { label: t('sprawy.form.description'), value: sprawa.description },
    { label: t('sprawy.form.procedure'), value: sprawa.procedure },
    { label: t('sprawy.form.settlement'), value: sprawa.settlement },
    { label: t('sprawy.form.mediationStatus'), value: sprawa.mediationStatus },
  ];

  return (
    <PageContent>
      <DetailHeader
        onBack={() => navigate(-1)}
        title={sprawa.signature}
        meta={
          <>
            <Badge bg="secondary">{t(`sprawaStatus.${sprawa.portalStatus}`)}</Badge>
            {sprawa.statusName && (
              <StatusBadge $color={sprawa.statusColor ?? undefined}>
                {sprawa.statusName}
              </StatusBadge>
            )}
            <span>{formatDate(sprawa.createdAt)}</span>
          </>
        }
      />

      <Tabs defaultActiveKey="info" className="mb-3">
        <Tab eventKey="info" title={t('sprawy.detail.info')}>
          <DefinitionList items={infoRows} />

          {sprawa.courtInfo && (
            <div className="mt-4">
              <h6 className="text-muted border-bottom pb-2">{t('sprawy.detail.courtInfo')}</h6>
              <DefinitionList
                items={[
                  {
                    label: t('courts.table.name'),
                    value: <Link to={`/courts/${sprawa.courtInfo.id}`}>{sprawa.courtInfo.name}</Link>,
                  },
                  { label: t('courts.table.address'), value: sprawa.courtInfo.address },
                ]}
              />
            </div>
          )}
        </Tab>

        <Tab eventKey="parties" title={`${t('sprawy.detail.parties')} (${sprawa.parties?.length ?? 0})`}>
          {!sprawa.parties?.length ? (
            <p className="text-muted">{t('sprawy.detail.noParties')}</p>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th>{t('metadata.partyName')}</th>
                  <th>{t('metadata.partyRole')}</th>
                </tr>
              </thead>
              <tbody>
                {sprawa.parties.map((p) => (
                  <tr key={p.id}>
                    <td><Link to={`/customers/${p.customerId}`}>{p.customerName}</Link></td>
                    <td>{p.role ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        <Tab eventKey="documents" title={`${t('sprawy.detail.documents')} (${sprawa.documents?.length ?? 0})`}>
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="primary"
              size="sm"
              onClick={() => documentInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <><Spinner size="sm" className="me-1" />{t('sprawy.detail.uploading')}</>
              ) : (
                <><HiPlus size="1rem" className="me-1" />{t('sprawy.detail.addDocument')}</>
              )}
            </Button>
            <HiddenFileInput
              ref={documentInputRef}
              accept=".pdf,.docx,.rtf,.jpg,.jpeg,.png"
              multiple
              onChange={handleDocumentUpload}
            />
          </div>
          {!sprawa.documents?.length ? (
            <p className="text-muted">{t('sprawy.detail.noDocuments')}</p>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th>{t('documents.table.name')}</th>
                  <th>{t('documents.table.status')}</th>
                  <th>{t('documents.table.uploaded')}</th>
                </tr>
              </thead>
              <tbody>
                {sprawa.documents.map((d) => (
                  <tr key={d.id}>
                    <td><Link to={`/documents/${d.id}`}>{d.fileName}</Link></td>
                    <td>{t(`status.${d.status.toLowerCase()}`)}</td>
                    <td>{formatDate(d.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        <Tab eventKey="posiedzenia" title={`${t('sprawy.detail.posiedzenia')} (${sprawa.posiedzenia?.length ?? 0})`}>
          <div className="d-flex justify-content-end mb-3">
            <Button variant="primary" size="sm" onClick={openCreatePosiedzenie}>
              <HiPlus size="1rem" className="me-1" />
              {t('posiedzenia.addPosiedzenie')}
            </Button>
          </div>
          {!sprawa.posiedzenia?.length ? (
            <p className="text-muted">{t('sprawy.detail.noPosiedzenia')}</p>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th>{t('posiedzenia.form.date')}</th>
                  <th>{t('posiedzenia.form.type')}</th>
                  <th>{t('posiedzenia.form.location')}</th>
                  <th>{t('posiedzenia.form.notes')}</th>
                  <ActionsTh />
                </tr>
              </thead>
              <tbody>
                {sprawa.posiedzenia.map((p) => (
                  <tr key={p.id}>
                    <td>{formatDateTime(p.date)}</td>
                    <td>{t(`posiedzenieType.${p.type}`)}</td>
                    <td>{p.location ?? '—'}</td>
                    <td>{p.notes ?? '—'}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button variant="outline-primary" size="sm" onClick={() => openEditPosiedzenie(p)}>
                          <HiPencil size="0.875rem" />
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeletePosiedzenie(p)}>
                          <HiTrash size="0.875rem" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        <Tab eventKey="timeEntries" title={`${t('timeEntries.tab')} (${timeEntries.length})`}>
          <TimeEntriesTab sprawaId={id!} />
        </Tab>
      </Tabs>

      <PosiedzenieFormModal
        show={showPosiedzenieModal}
        onClose={() => { setShowPosiedzenieModal(false); setEditingPosiedzenie(null); }}
        onSubmit={editingPosiedzenie ? handleUpdatePosiedzenie : handleCreatePosiedzenie}
        initialData={editingPosiedzenie}
        sprawaId={id}
      />

      <ConfirmDialog {...confirmProps} />
    </PageContent>
  );
}
